import type { toast as Toast } from 'react-toastify';
import type { AnyAction, Middleware } from 'redux';
import { FormattedMessage } from '@/components/formatted_message/FormattedMessage';
import { StyledLink } from '@/components/link/components/StyledLink';
import { ACTION_FAILED, FETCHING_DATA_FAILED } from '@/containers/App/constants';
import { SupplyPointDetailTab } from '@/routes/establishments/routes/supply_points/routes/detail/enums/SupplyPointDetailTab';
import {
  createFavouriteError,
  createFavouriteSuccess,
  deleteFavouriteError,
  deleteFavouriteSuccess,
  updateFavouriteError,
  updateFavouriteSuccess,
} from '@/containers/favourites';
import {
  notificationMuted,
  notificationArchived,
  notificationResolved,
  notificationReturnedToList,
  notificationReturnedToUnresolved,
  notificationSettingsArchived,
  notificationSettingsActivated,
  notificationSettingsRestored,
  notificationSettingsSuspended,
  notificationSettingsChangesSaved,
  notificationSettingsCreated,
  notificationSettingsSaveFailed,
  notificationUnmuted,
} from '@/containers/App/actions';
import {
  createEmissionRecordSuccessAction,
  deleteEmissionRecordSuccessAction,
  updateEmissionRecordSuccessAction,
} from '@/routes/carbon_footprint/routes/emissions/routes/records/actions';
import {
  notifyCreateValidityError,
  notifyCreateValiditySuccess,
  notifyDeleteValidityError,
  notifyDeleteValiditySuccess,
} from '@/routes/carbon_footprint/routes/emissions_factor_detail/actions';
import {
  deleteExportErrorAction,
  deleteExportSuccessAction,
  editExportErrorAction,
  editExportSuccessAction,
  forceRunExportErrorAction,
  forceRunExportSuccessAction,
  isExportDataReadyAction,
  toggleActiveExportErrorAction,
  toggleActiveExportSuccessAction,
} from '@/routes/exports/actions';
import { CREATE_EXPORT_ERROR, CREATE_EXPORT_SUCCESS, EXPORT_DATA_READY } from '@/routes/exports/constants';
import {
  createManualRecordErrorAction,
  createManualRecordSuccessAction,
  deleteManualRecordErrorAction,
  deleteManualRecordSuccessAction,
  updateManualRecordErrorAction,
  updateManualRecordSuccessAction,
} from '@/routes/records/routes/manual/actions';
import { ConflictError, ValidationFailedError } from '@/utils/request';
import {
  askNewMeterModelActions,
  authActions,
  billingActions,
  billingSettingsActions,
  bubbleTourActions,
  bugReportActions,
  conflictErrorAction,
  createEmissionFactorErrorAction,
  createEmissionFactorSuccessAction,
  dashboardActions,
  deleteEmissionFactorErrorAction,
  deleteEmissionFactorSuccessAction,
  editEmissionFactorErrorAction,
  editEmissionFactorSuccessAction,
  establishmentActions,
  fileActions,
  floorPlanActions,
  leaseActions,
  leasesActions,
  leaseUnitActions,
  metersActions,
  readingActions,
  recordsActions,
  supplyPointActions,
  tenantActions,
  userActions,
} from './actions';
import { getDynamicErrorTranslation, Notifications as messages } from './messages';
import { DownloadExportLink } from '@/routes/exports/components/DownloadLink';

export const createNotificationsMiddleware =
  (toast: typeof Toast): Middleware =>
  () =>
  next =>
  (action: AnyAction) => {
    // propagate action into reducers on first place
    const result = next(action);

    // then show action related notification
    switch (action.type) {
      case FETCHING_DATA_FAILED:
        toast.error(<FormattedMessage {...messages.fetchingDataFailed} />, {
          position: toast.POSITION.BOTTOM_RIGHT,
          toastId: FETCHING_DATA_FAILED,
        });
        break;
      case ACTION_FAILED:
        toast.error(<FormattedMessage {...messages.actionFailed} />, {
          position: toast.POSITION.BOTTOM_RIGHT,
          toastId: ACTION_FAILED,
        });
        break;

      case CREATE_EXPORT_SUCCESS:
        toast(<FormattedMessage {...messages.exportCreateSuccess} />, {
          position: toast.POSITION.BOTTOM_RIGHT,
          toastId: CREATE_EXPORT_SUCCESS,
        });
        break;
      case EXPORT_DATA_READY:
        if (isExportDataReadyAction(action)) {
          toast(
            <FormattedMessage
              {...messages.exportDataReady}
              values={{
                link: <DownloadExportLink exportId={action.payload._id} />,
              }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: EXPORT_DATA_READY,
            },
          );
        }
        break;
      case CREATE_EXPORT_ERROR:
        toast.error(<FormattedMessage {...messages.exportCreateError} />, {
          position: toast.POSITION.BOTTOM_RIGHT,
          toastId: CREATE_EXPORT_ERROR,
        });
        break;
      default:
        // AUTH RESET PASSWORD
        if (authActions.resetPassword.success.match(action)) {
          toast(<FormattedMessage {...messages.resetPasswordSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: authActions.resetPassword.success.type,
          });
        } else if (authActions.resetPassword.error.match(action)) {
          toast.error(<FormattedMessage {...messages.resetPasswordError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: authActions.resetPassword.error.type,
          });
        }

        // AUTH SET PASSWORD
        else if (authActions.setPassword.success.match(action)) {
          toast(<FormattedMessage {...messages.setPasswordSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: authActions.setPassword.success.type,
          });
        }

        // AUTH LOGIN
        else if (authActions.login.success.match(action)) {
          toast(<FormattedMessage {...messages.loginSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: authActions.login.success.type,
          });
        }

        // AUTH LOGOUT
        else if (authActions.logout.success.match(action)) {
          toast(<FormattedMessage {...messages.logoutSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: authActions.logout.success.type,
          });
        } else if (authActions.logout.error.match(action)) {
          toast.error(<FormattedMessage {...messages.logoutError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: authActions.logout.error.type,
          });
        }

        // AUTH OTP
        else if (authActions.otp.success.match(action)) {
          toast(<FormattedMessage {...messages.otpSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: authActions.otp.success.type,
          });
        }

        // AUTH LOGIN TO CUSTOMER
        else if (authActions.loginToCustomer.error.match(action)) {
          toast.error(<FormattedMessage {...messages.loginToCustomerError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: authActions.loginToCustomer.error.type,
          });
        }

        // AUTH LOGOUT FROM CUSTOMER
        else if (authActions.logoutFromCustomer.error.match(action)) {
          toast.error(<FormattedMessage {...messages.logoutFromCustomerError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: authActions.logoutFromCustomer.error.type,
          });
        }

        // Auth disable 2fa
        else if (authActions.twoFactor.disable.success.match(action)) {
          toast(<FormattedMessage {...messages.twoFactorDisableSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: authActions.twoFactor.disable.success.type,
          });
        } else if (authActions.twoFactor.disable.error.match(action)) {
          toast.error(<FormattedMessage {...messages.twoFactorDisableError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: authActions.twoFactor.disable.error.type,
          });
        }

        // Auth verify totp
        else if (authActions.twoFactor.verifyTotp.success.match(action)) {
          toast(<FormattedMessage {...messages.twoFactorVerifyTotpSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: authActions.twoFactor.verifyTotp.success.type,
          });
        }

        // Auth verify backup code
        else if (authActions.twoFactor.verifyBackupCode.success.match(action)) {
          toast(<FormattedMessage {...messages.twoFactorVerifyBackupCodeSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: authActions.twoFactor.verifyBackupCode.success.type,
          });
        }

        // CHANGE PASSWORD
        else if (userActions.changePassword.success.match(action)) {
          toast(<FormattedMessage {...messages.changePasswordSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: userActions.changePassword.success.type,
          });
        }

        // CREATE ESTABLISHMENT
        if (establishmentActions.create.success.match(action)) {
          toast(<FormattedMessage {...messages.createEstablishmentSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (establishmentActions.create.error.match(action)) {
          toast.error(<FormattedMessage {...messages.createEstablishmentError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        }
        // IMPORT ESTABLISHMENT
        if (establishmentActions.import.success.match(action)) {
          toast(<FormattedMessage {...messages.importEstablishmentSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (establishmentActions.import.error.match(action)) {
          toast.error(<FormattedMessage {...messages.importEstablishmentError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        }
        // EDIT ESTABLISHMENT
        else if (establishmentActions.update.success.match(action)) {
          toast(<FormattedMessage {...messages.editEstablishmentSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (establishmentActions.update.error.match(action)) {
          toast.error(<FormattedMessage {...messages.editEstablishmentError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        }
        // DELETE ESTABLISHMENT
        else if (establishmentActions.delete.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteEstablishmentSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (establishmentActions.delete.error.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteEstablishmentError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        }

        // BULK ADD TAGS TO ESTABLISHMENTS
        else if (establishmentActions.bulkAddTags.success.match(action)) {
          const { succeededCount, skippedCount } = action.payload;
          if (succeededCount === 0) {
            toast.error(<FormattedMessage {...messages.bulkAddTagsEstablishmentFailed} values={{ skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          } else {
            toast(
              <FormattedMessage
                {...messages.bulkAddTagsEstablishmentSuccess}
                values={{ succeededCount, skippedCount }}
              />,
              {
                position: toast.POSITION.BOTTOM_RIGHT,
                toastId: action.type,
              },
            );
          }
        } else if (establishmentActions.bulkAddTags.error.match(action)) {
          toast.error(
            <FormattedMessage {...messages.bulkAddTagsEstablishmentError} values={{ count: action.payload.count }} />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        }

        // BULK REMOVE TAGS FROM ESTABLISHMENTS
        else if (establishmentActions.bulkRemoveTags.success.match(action)) {
          const { succeededCount, skippedCount } = action.payload;
          if (succeededCount === 0) {
            toast.error(
              <FormattedMessage {...messages.bulkRemoveTagsEstablishmentFailed} values={{ skippedCount }} />,
              {
                position: toast.POSITION.BOTTOM_RIGHT,
                toastId: action.type,
              },
            );
          } else {
            toast(
              <FormattedMessage
                {...messages.bulkRemoveTagsEstablishmentSuccess}
                values={{ succeededCount, skippedCount }}
              />,
              {
                position: toast.POSITION.BOTTOM_RIGHT,
                toastId: action.type,
              },
            );
          }
        } else if (establishmentActions.bulkRemoveTags.error.match(action)) {
          toast.error(
            <FormattedMessage
              {...messages.bulkRemoveTagsEstablishmentError}
              values={{ count: action.payload.count }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        }

        // MOVE SUPPLY POINT
        else if (supplyPointActions.move.success.match(action)) {
          const { _id: supplyPointId, name } = action.payload;
          toast(
            <FormattedMessage
              {...messages.moveSupplyPointSingleSuccess}
              values={{
                supplyPoint: (
                  <StyledLink
                    to={`/establishments/supply-points/detail/${supplyPointId}/${SupplyPointDetailTab.Overview}`}
                  >
                    {name}
                  </StyledLink>
                ),
              }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
            },
          );
        } else if (supplyPointActions.move.error.match(action)) {
          const { _id: supplyPointId, name } = action.payload;
          toast.error(
            <FormattedMessage
              {...messages.moveSupplyPointSingleError}
              values={{
                supplyPoint: (
                  <StyledLink
                    to={`/establishments/supply-points/detail/${supplyPointId}/${SupplyPointDetailTab.Overview}`}
                  >
                    {name}
                  </StyledLink>
                ),
              }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
            },
          );
        }

        // MOVE SUPPLY POINT BULK
        else if (supplyPointActions.moveBulk.success.match(action)) {
          const { succeededCount, skippedCount } = action.payload;
          if (succeededCount === 0) {
            toast.error(<FormattedMessage {...messages.moveSupplyPointFailed} values={{ skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          } else {
            toast(<FormattedMessage {...messages.moveSupplyPointSuccess} values={{ succeededCount, skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          }
        } else if (supplyPointActions.moveBulk.error.match(action)) {
          toast.error(
            <FormattedMessage {...messages.moveSupplyPointError} values={{ count: action.payload.count }} />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        }

        // SUSPEND SUPPLY POINTS
        else if (supplyPointActions.suspend.success.match(action)) {
          const { succeededCount, skippedCount } = action.payload;
          if (succeededCount === 0) {
            toast.error(<FormattedMessage {...messages.suspendSupplyPointsFailed} values={{ skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          } else {
            toast(
              <FormattedMessage {...messages.suspendSupplyPointsSuccess} values={{ succeededCount, skippedCount }} />,
              {
                position: toast.POSITION.BOTTOM_RIGHT,
                toastId: action.type,
              },
            );
          }
        } else if (supplyPointActions.suspend.error.match(action)) {
          toast.error(
            <FormattedMessage {...messages.suspendSupplyPointsError} values={{ count: action.payload.count }} />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        }

        // TOGGLE SUPPLY POINT STATUS
        else if (supplyPointActions.toggleStatus.success.match(action)) {
          toast(
            <FormattedMessage
              {...messages.toggleSupplyPointStatusSuccess}
              values={{ name: action.payload.name, status: action.payload.status }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        } else if (supplyPointActions.toggleStatus.error.match(action)) {
          toast.error(
            <FormattedMessage
              {...messages.toggleSupplyPointStatusError}
              values={{ name: action.payload.name, status: action.payload.status }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        } else if (
          supplyPointActions.revision.createBulk.success.match(action) ||
          supplyPointActions.editBulk.success.match(action)
        ) {
          const { succeededCount, skippedCount } = action.payload;
          if (succeededCount === 0) {
            toast.error(<FormattedMessage {...messages.bulkChangeFailed} values={{ skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          } else {
            toast(<FormattedMessage {...messages.bulkChangeSuccess} values={{ succeededCount, skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          }
        } else if (
          supplyPointActions.revision.createBulk.error.match(action) ||
          supplyPointActions.editBulk.error.match(action)
        ) {
          toast.error(<FormattedMessage {...messages.bulkChangeError} values={{ count: action.payload.count }} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        }

        // SUPPLY POINT REVISION
        else if (supplyPointActions.revision.create.success.match(action)) {
          toast(<FormattedMessage {...messages.createSupplyPointRevisionSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (supplyPointActions.revision.create.error.match(action)) {
          toast.error(<FormattedMessage {...messages.createSupplyPointRevisionError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (supplyPointActions.revision.update.success.match(action)) {
          toast(<FormattedMessage {...messages.editSupplyPointRevisionSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (supplyPointActions.revision.update.error.match(action)) {
          toast.error(<FormattedMessage {...messages.editSupplyPointRevisionError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        }

        // CREATE METER
        else if (metersActions.create.success.match(action)) {
          const { name } = action.payload;

          toast(<FormattedMessage {...messages.createMeterSuccess} values={{ name }} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.create.success.type,
          });
        } else if (metersActions.create.error.match(action)) {
          toast.error(<FormattedMessage {...messages.createMeterError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.create.error.type,
          });
        }

        // EDIT METER
        else if (metersActions.update.success.match(action)) {
          const { name } = action.payload;

          toast(<FormattedMessage {...messages.editMeterSuccess} values={{ name }} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.update.success.type,
          });
        } else if (metersActions.update.error.match(action)) {
          toast.error(<FormattedMessage {...messages.editMeterError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.update.error.type,
          });
        }

        // DELETE METER
        else if (metersActions.delete.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteMeterSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.delete.success.type,
          });
        } else if (metersActions.delete.error.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteMeterError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.delete.error.type,
          });
        }

        // MOVE METER
        else if (metersActions.move.success.match(action)) {
          const { _id: meterId, name } = action.payload;
          toast(
            <FormattedMessage
              {...messages.moveMeterSuccess}
              values={{
                meter: <StyledLink to={`/establishments/meters/detail/${meterId}/details`}>{name}</StyledLink>,
              }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
            },
          );
        } else if (metersActions.move.error.match(action)) {
          const { _id: meterId, name } = action.payload;
          toast.error(
            <FormattedMessage
              {...messages.moveMeterError}
              values={{
                meter: <StyledLink to={`/establishments/meters/detail/${meterId}/details`}>{name}</StyledLink>,
              }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
            },
          );
        }

        // MOVE METERS BULK
        if (metersActions.moveBulk.success.match(action)) {
          const { succeededCount, skippedCount } = action.payload;
          if (succeededCount === 0) {
            toast.error(<FormattedMessage {...messages.moveMetersBulkFailed} values={{ skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          } else {
            toast(<FormattedMessage {...messages.moveMetersBulkSuccess} values={{ succeededCount, skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          }
        } else if (metersActions.moveBulk.error.match(action)) {
          toast.error(<FormattedMessage {...messages.moveMetersBulkError} values={{ count: action.payload.count }} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        }

        // SET PARENT METERS BULK
        else if (metersActions.setParentBulk.success.match(action)) {
          const { succeededCount, skippedCount } = action.payload;
          if (succeededCount === 0) {
            toast.error(<FormattedMessage {...messages.setParentMetersBulkFailed} values={{ skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          } else {
            toast(
              <FormattedMessage {...messages.setParentMetersBulkSuccess} values={{ succeededCount, skippedCount }} />,
              {
                position: toast.POSITION.BOTTOM_RIGHT,
                toastId: action.type,
              },
            );
          }
        } else if (metersActions.setParentBulk.error.match(action)) {
          toast.error(
            <FormattedMessage {...messages.setParentMetersBulkError} values={{ count: action.payload.count }} />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        } else if (metersActions.setPlacementBulk.success.match(action)) {
          const { succeededCount, skippedCount } = action.payload;
          if (succeededCount === 0) {
            toast.error(<FormattedMessage {...messages.setPlacementMetersBulkFailed} values={{ skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          } else {
            toast(
              <FormattedMessage
                {...messages.setPlacementMetersBulkSuccess}
                values={{ succeededCount, skippedCount }}
              />,
              {
                position: toast.POSITION.BOTTOM_RIGHT,
                toastId: action.type,
              },
            );
          }
        } else if (metersActions.setPlacementBulk.error.match(action)) {
          toast.error(
            <FormattedMessage {...messages.setPlacementMetersBulkError} values={{ count: action.payload.count }} />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        }

        // REPORT METER
        if (metersActions.report.success.match(action)) {
          toast(<FormattedMessage {...messages.reportMeterFaultSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.report.success.type,
          });
        } else if (metersActions.report.error.match(action)) {
          toast.error(<FormattedMessage {...messages.reportMeterFaultError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.report.error.type,
          });
        }

        // REPLACE METER
        else if (metersActions.replace.success.match(action)) {
          toast(<FormattedMessage {...messages.replaceMeterSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.replace.success.type,
          });
        } else if (metersActions.replace.error.match(action)) {
          toast.error(<FormattedMessage {...messages.replaceMeterError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.replace.error.type,
          });
        }

        // CREATE METER REGISTER
        else if (metersActions.registers.create.success.match(action)) {
          toast(<FormattedMessage {...messages.createMeterRegisterSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.registers.create.success.type,
          });
        } else if (metersActions.registers.create.error.match(action)) {
          toast.error(<FormattedMessage {...messages.createMeterRegisterError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.registers.create.error.type,
          });
        }

        // UPDATE METER REGISTER
        else if (metersActions.registers.update.success.match(action)) {
          toast(<FormattedMessage {...messages.updateMeterRegisterSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.registers.update.error.type,
          });
        } else if (metersActions.registers.update.error.match(action)) {
          toast.error(<FormattedMessage {...messages.updateMeterRegisterError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.registers.update.error.type,
          });
        }

        // DELETE METER REGISTER
        else if (metersActions.registers.delete.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteMeterRegisterSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.registers.delete.success.type,
          });
        } else if (metersActions.registers.delete.error.match(action)) {
          if (action.payload instanceof ValidationFailedError) {
            const validationError = action.payload;
            validationError.errors.forEach(error => {
              toast.error(
                <FormattedMessage {...getDynamicErrorTranslation(`${error.path.join('.')}.${error.type}`)} />,
                {
                  position: toast.POSITION.BOTTOM_RIGHT,
                },
              );
            });
          } else {
            toast.error(<FormattedMessage {...messages.deleteMeterRegisterError} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
            });
          }
        }

        // EDIT METER REVISION
        else if (metersActions.revisions.update.success.match(action)) {
          toast(<FormattedMessage {...messages.editRevisionSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.revisions.update.success.type,
          });
        } else if (metersActions.revisions.update.error.match(action)) {
          toast.error(<FormattedMessage {...messages.editRecordError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.revisions.update.error.type,
          });
        }

        // REVERT METER REVISION
        else if (metersActions.revisions.revert.success.match(action)) {
          toast(<FormattedMessage {...messages.editRevisionSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.revisions.revert.success.type,
          });
        } else if (metersActions.revisions.revert.error.match(action)) {
          toast.error(<FormattedMessage {...messages.editRecordError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.revisions.revert.error.type,
          });
        }

        // DELETE METER REVISION
        else if (metersActions.revisions.delete.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteRevisionSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.revisions.delete.success.type,
          });
        } else if (metersActions.revisions.delete.error.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteRevisionError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: metersActions.revisions.delete.error.type,
          });
        }

        // CREATE USER
        if (userActions.create.success.match(action)) {
          toast(<FormattedMessage {...messages.createUserSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (userActions.create.error.match(action)) {
          toast.error(<FormattedMessage {...messages.createUserError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        }

        // EDIT USER
        else if (userActions.update.success.match(action)) {
          toast(<FormattedMessage {...messages.editUserSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (userActions.update.error.match(action)) {
          toast.error(<FormattedMessage {...messages.editUserError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        }

        // DELETE USER
        else if (userActions.delete.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteUserSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (userActions.delete.error.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteUserError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        }

        // DELETE USERS BULK
        else if (userActions.deleteBulk.success.match(action)) {
          const { succeededCount, skippedCount } = action.payload;
          if (succeededCount === 0) {
            toast.error(<FormattedMessage {...messages.deleteUsersBulkFailed} values={{ skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          } else {
            toast(<FormattedMessage {...messages.deleteUsersBulkSuccess} values={{ succeededCount, skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          }
        } else if (userActions.deleteBulk.error.match(action)) {
          toast.error(
            <FormattedMessage {...messages.deleteUsersBulkError} values={{ count: action.payload.count }} />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        }

        // UPDATE USERS PERMISSIONS BULK
        else if (userActions.updatePermissionsBulk.success.match(action)) {
          const { succeededCount, skippedCount } = action.payload;
          if (succeededCount === 0) {
            toast.error(<FormattedMessage {...messages.updateUsersPermissionsBulkFailed} values={{ skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          } else {
            toast(
              <FormattedMessage
                {...messages.updateUsersPermissionsBulkSuccess}
                values={{ succeededCount, skippedCount }}
              />,
              {
                position: toast.POSITION.BOTTOM_RIGHT,
                toastId: action.type,
              },
            );
          }
        } else if (userActions.updatePermissionsBulk.error.match(action)) {
          toast.error(
            <FormattedMessage {...messages.updateUsersPermissionsBulkError} values={{ count: action.payload.count }} />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        }

        // UPDATE USERS ACCESSES BULK
        else if (userActions.updateAccessesBulk.success.match(action)) {
          const { succeededCount, skippedCount } = action.payload;
          if (succeededCount === 0) {
            toast.error(<FormattedMessage {...messages.updateUsersAccessesBulkFailed} values={{ skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          } else {
            toast(
              <FormattedMessage
                {...messages.updateUsersAccessesBulkSuccess}
                values={{ succeededCount, skippedCount }}
              />,
              {
                position: toast.POSITION.BOTTOM_RIGHT,
                toastId: action.type,
              },
            );
          }
        } else if (userActions.updateAccessesBulk.error.match(action)) {
          toast.error(
            <FormattedMessage {...messages.updateUsersAccessesBulkError} values={{ count: action.payload.count }} />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        }

        // RESET USERS PASSWORD BULK
        else if (userActions.resetPasswordBulk.success.match(action)) {
          const { succeededCount, skippedCount } = action.payload;
          if (succeededCount === 0) {
            toast.error(<FormattedMessage {...messages.resetUsersPasswordBulkFailed} values={{ skippedCount }} />, {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            });
          } else {
            toast(
              <FormattedMessage
                {...messages.resetUsersPasswordBulkSuccess}
                values={{ succeededCount, skippedCount }}
              />,
              {
                position: toast.POSITION.BOTTOM_RIGHT,
                toastId: action.type,
              },
            );
          }
        } else if (userActions.resetPasswordBulk.error.match(action)) {
          toast.error(
            <FormattedMessage {...messages.resetUsersPasswordBulkError} values={{ count: action.payload.count }} />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        }

        // CHANGE USER SETTINGS
        else if (userActions.updateMySettings.success.match(action)) {
          toast(<FormattedMessage {...messages.editUserSettingsSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (userActions.updateMySettings.error.match(action)) {
          toast.error(<FormattedMessage {...messages.editUserSettingsError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        }

        // CREATE USER FAVOURITE
        else if (createFavouriteSuccess.match(action)) {
          toast(<FormattedMessage {...messages.createFavouriteSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        } else if (createFavouriteError.match(action)) {
          toast.error(<FormattedMessage {...messages.createFavouriteError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }

        // UPDATE USER FAVOURITE
        else if (updateFavouriteSuccess.match(action)) {
          toast(<FormattedMessage {...messages.updateFavouriteSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        } else if (updateFavouriteError.match(action)) {
          toast.error(<FormattedMessage {...messages.updateFavouriteError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }

        // DELETE USER FAVOURITE
        else if (deleteFavouriteSuccess.match(action)) {
          toast(<FormattedMessage {...messages.deleteFavouriteSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        } else if (deleteFavouriteError.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteFavouriteError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }

        // USER ONBOARDING BUBBLE TOUR
        else if (bubbleTourActions.changeStep.error.match(action)) {
          toast.error(<FormattedMessage {...messages.changeStepBubbleTourError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }

        // DASHBOARD LOAD ERROR
        else if (dashboardActions.load.error.match(action)) {
          toast.error(<FormattedMessage {...messages.dashboardLoadError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }

        // DASHBOARD USER SUGGESTION ACKNOWLEDGMENT
        else if (dashboardActions.suggestionAcknowledgment.success.match(action)) {
          toast(<FormattedMessage {...messages.dashboardUserSuggestionAcknowledgmentSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        } else if (dashboardActions.suggestionAcknowledgment.error.match(action)) {
          toast.error(<FormattedMessage {...messages.dashboardUserSuggestionAcknowledgmentError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }

        // GET SUPPLY POINTS DROPDOWN OPTIONS ERROR
        else if (supplyPointActions.getMetadata.error.match(action)) {
          toast.error(<FormattedMessage {...messages.getSupplyPointsMetadataError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        } else if (supplyPointActions.delete.error.match(action)) {
          toast.error(
            <FormattedMessage {...messages.deleteSupplyPointError} values={{ count: action.payload.count }} />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        } else if (supplyPointActions.delete.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteSupplyPointSuccess} values={{ count: action.payload.count }} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (supplyPointActions.update.success.match(action)) {
          toast(<FormattedMessage {...messages.updateSupplyPointSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (supplyPointActions.update.error.match(action)) {
          toast.error(<FormattedMessage {...messages.updateSupplyPointError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (recordsActions.update.success.match(action)) {
          toast(<FormattedMessage {...messages.editRecordSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: recordsActions.update.success.type,
          });
        } else if (recordsActions.update.error.match(action)) {
          toast.error(<FormattedMessage {...messages.editRecordError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: recordsActions.update.error.type,
          });
        } else if (recordsActions.delete.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteRecordSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: recordsActions.delete.success.type,
          });
        } else if (recordsActions.delete.error.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteRecordError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: recordsActions.delete.error.type,
          });
        } else if (recordsActions.deleteMany.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteRecordsSuccess} values={{ count: action.payload.count }} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: recordsActions.deleteMany.success.type,
          });
        } else if (recordsActions.deleteMany.error.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteRecordsError} values={{ count: action.payload.count }} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: recordsActions.deleteMany.error.type,
          });
        } else if (recordsActions.create.success.match(action)) {
          toast(<FormattedMessage {...messages.createRecordSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: recordsActions.create.success.type,
          });
        } else if (recordsActions.create.error.match(action)) {
          toast.error(<FormattedMessage {...messages.createRecordError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: recordsActions.create.error.type,
          });
        } else if (fileActions.deleteFile.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteFileSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: fileActions.deleteFile.success.type,
          });
        } else if (fileActions.deleteFile.error.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteFileError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: fileActions.deleteFile.error.type,
          });
        } else if (fileActions.uploadFile.success.match(action)) {
          toast(<FormattedMessage {...messages.uploadFileSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: fileActions.uploadFile.success.type,
          });
        } else if (fileActions.uploadFile.error.match(action)) {
          toast.error(<FormattedMessage {...messages.uploadFileError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: fileActions.uploadFile.success.type,
          });
        } else if (fileActions.uploadUnsupportedFile.match(action)) {
          toast.error(<FormattedMessage {...messages.unsupportedFileError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: fileActions.uploadFile.success.type,
          });
        } else if (fileActions.uploadFiles.success.match(action)) {
          toast(<FormattedMessage {...messages.uploadFilesSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: fileActions.uploadFiles.success.type,
          });
        } else if (fileActions.uploadFiles.error.match(action)) {
          toast.error(<FormattedMessage {...messages.uploadFilesError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: fileActions.uploadFiles.error.type,
          });
        } else if (fileActions.edit.success.match(action)) {
          toast(<FormattedMessage {...messages.editFileSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: fileActions.edit.success.type,
          });
        } else if (fileActions.edit.error.match(action)) {
          toast.error(<FormattedMessage {...messages.editFileError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: fileActions.edit.error.type,
          });
        } else if (fileActions.downloadFile.error.match(action)) {
          toast.error(<FormattedMessage {...messages.downloadFileError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: fileActions.downloadFile.error.type,
          });
        } else if (fileActions.fileLimit.error.match(action)) {
          toast.error(<FormattedMessage {...messages.fileLimitError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: fileActions.uploadFiles.error.type,
          });
        } else if (fileActions.validation.error.match(action)) {
          toast.error(<FormattedMessage {...messages.fileValidationError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: fileActions.validation.error.type,
          });
        } else if (notificationSettingsSaveFailed.match(action)) {
          toast.error(<FormattedMessage {...messages.saveNotificationSettingFailed} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notificationSettingsSaveFailed.type,
          });
        } else if (notificationSettingsCreated.match(action)) {
          toast(<FormattedMessage {...messages.saveNotificationSettingSucceeded} values={{ name: action.payload }} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notificationSettingsCreated.type,
          });
        } else if (notificationResolved.match(action)) {
          toast(<FormattedMessage {...messages.notificationResolved} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notificationResolved.type,
          });
        } else if (notificationReturnedToUnresolved.match(action)) {
          toast(<FormattedMessage {...messages.notificationReturnedToUnresolved} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notificationReturnedToUnresolved.type,
          });
        } else if (notificationReturnedToList.match(action)) {
          toast(<FormattedMessage {...messages.notificationReturnedToList} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notificationReturnedToList.type,
          });
        } else if (notificationArchived.match(action)) {
          toast(<FormattedMessage {...messages.notificationArchived} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notificationArchived.type,
          });
        } else if (notificationMuted.match(action)) {
          toast(<FormattedMessage {...messages.notificationMuted} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notificationMuted.type,
          });
        } else if (notificationUnmuted.match(action)) {
          toast(<FormattedMessage {...messages.notificationUnmuted} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notificationUnmuted.type,
          });
        } else if (notificationSettingsChangesSaved.match(action)) {
          toast(<FormattedMessage {...messages.notificationSettingsChangesSaved} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notificationSettingsChangesSaved.type,
          });
        } else if (notificationSettingsArchived.match(action)) {
          toast(<FormattedMessage {...messages.notificationSettingsArchived} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notificationSettingsArchived.type,
          });
        } else if (notificationSettingsRestored.match(action)) {
          toast(<FormattedMessage {...messages.notificationSettingsRestored} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notificationSettingsRestored.type,
          });
        } else if (notificationSettingsSuspended.match(action)) {
          toast(<FormattedMessage {...messages.notificationSettingsSuspended} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notificationSettingsSuspended.type,
          });
        } else if (notificationSettingsActivated.match(action)) {
          toast(<FormattedMessage {...messages.notificationSettingsActivated} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notificationSettingsActivated.type,
          });
        } else if (createEmissionFactorSuccessAction.match(action)) {
          toast(<FormattedMessage {...messages.emissionFactorCreateSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: createEmissionFactorSuccessAction.type,
          });
        } else if (createEmissionFactorErrorAction.match(action)) {
          toast.error(<FormattedMessage {...messages.emissionFactorCreateError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: createEmissionFactorErrorAction.type,
          });
        } else if (deleteEmissionFactorSuccessAction.match(action)) {
          toast(<FormattedMessage {...messages.emissionFactorDeleteSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: deleteEmissionFactorSuccessAction.type,
          });
        } else if (deleteEmissionFactorErrorAction.match(action)) {
          toast.error(<FormattedMessage {...messages.emissionFactorDeleteError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: deleteEmissionFactorErrorAction.type,
          });
        } else if (editEmissionFactorSuccessAction.match(action)) {
          toast(<FormattedMessage {...messages.emissionFactorEditSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: editEmissionFactorSuccessAction.type,
          });
        } else if (editEmissionFactorErrorAction.match(action)) {
          toast.error(<FormattedMessage {...messages.emissionFactorEditError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: editEmissionFactorErrorAction.type,
          });
        } else if (updateEmissionRecordSuccessAction.match(action)) {
          toast(<FormattedMessage {...messages.emissionRecordEditSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: updateEmissionRecordSuccessAction.type,
          });
        } else if (createEmissionRecordSuccessAction.match(action)) {
          toast(<FormattedMessage {...messages.emissionRecordCreateSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: createEmissionRecordSuccessAction.type,
          });
        } else if (deleteEmissionRecordSuccessAction.match(action)) {
          toast(<FormattedMessage {...messages.emissionRecordDeleteSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: deleteEmissionRecordSuccessAction.type,
          });
        } else if (supplyPointActions.create.success.match(action)) {
          toast(<FormattedMessage {...messages.createSupplyPointSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (supplyPointActions.create.error.match(action)) {
          toast.error(<FormattedMessage {...messages.createSupplyPointError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (supplyPointActions.validation.error.match(action)) {
          toast.error(<FormattedMessage {...messages.createSupplyPointValidationError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (editExportSuccessAction.match(action)) {
          toast(<FormattedMessage {...messages.exportEditSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: editExportSuccessAction.type,
          });
        } else if (editExportErrorAction.match(action)) {
          toast.error(<FormattedMessage {...messages.exportEditError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: editExportErrorAction.type,
          });
        } else if (toggleActiveExportSuccessAction.match(action)) {
          const { active, name } = action.payload;
          toast(
            <FormattedMessage
              {...(active ? messages.exportSendingResumed : messages.exportSendingPaused)}
              values={{ name: <strong>{name}</strong> }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: toggleActiveExportSuccessAction.type,
            },
          );
        } else if (toggleActiveExportErrorAction.match(action)) {
          const { active, name } = action.payload;
          toast.error(
            <FormattedMessage
              {...(active ? messages.exportSendingResumedError : messages.exportSendingPausedError)}
              values={{ name: <strong>{name}</strong> }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: toggleActiveExportErrorAction.type,
            },
          );
        } else if (deleteExportSuccessAction.match(action)) {
          toast(<FormattedMessage {...messages.exportDeleteSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: deleteExportSuccessAction.type,
          });
        } else if (deleteExportErrorAction.match(action)) {
          toast.error(<FormattedMessage {...messages.exportDeleteError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: deleteExportErrorAction.type,
          });
        } else if (forceRunExportSuccessAction.match(action)) {
          toast(<FormattedMessage {...messages.exportForceRunSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: forceRunExportSuccessAction.type,
          });
        } else if (forceRunExportErrorAction.match(action)) {
          toast.error(<FormattedMessage {...messages.exportForceRunError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: forceRunExportErrorAction.type,
          });
        } else if (notifyDeleteValiditySuccess.match(action)) {
          toast(<FormattedMessage {...messages.deleteValiditySuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notifyDeleteValiditySuccess.type,
          });
        } else if (notifyDeleteValidityError.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteValidityError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notifyDeleteValidityError.type,
          });
        } else if (notifyCreateValiditySuccess.match(action)) {
          toast(<FormattedMessage {...messages.createValiditySuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notifyCreateValiditySuccess.type,
          });
        } else if (notifyCreateValidityError.match(action)) {
          toast.error(<FormattedMessage {...messages.createValidityError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: notifyCreateValidityError.type,
          });
        } else if (createManualRecordSuccessAction.match(action)) {
          toast(<FormattedMessage {...messages.createManualRecordSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: createManualRecordSuccessAction.type,
          });
        } else if (createManualRecordErrorAction.match(action)) {
          toast.error(<FormattedMessage {...messages.createManualRecordError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: createManualRecordErrorAction.type,
          });
        } else if (deleteManualRecordSuccessAction.match(action)) {
          toast(<FormattedMessage {...messages.deleteManualRecordSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: deleteManualRecordSuccessAction.type,
          });
        } else if (deleteManualRecordErrorAction.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteManualRecordError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: deleteManualRecordErrorAction.type,
          });
        } else if (updateManualRecordSuccessAction.match(action)) {
          toast(<FormattedMessage {...messages.updateManualRecordSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: updateManualRecordSuccessAction.type,
          });
        } else if (updateManualRecordErrorAction.match(action)) {
          toast.error(<FormattedMessage {...messages.updateManualRecordError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: updateManualRecordErrorAction.type,
          });
        } else if (tenantActions.create.success.match(action)) {
          toast(
            <FormattedMessage
              {...messages.createTenantSuccess}
              values={{
                name: <strong>{action.payload.name}</strong>,
              }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        } else if (tenantActions.create.error.match(action)) {
          toast.error(<FormattedMessage {...messages.createTenantError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (tenantActions.update.success.match(action)) {
          toast(
            <FormattedMessage
              {...messages.updateTenantSuccess}
              values={{
                name: <strong>{action.payload.name}</strong>,
              }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        } else if (tenantActions.update.error.match(action)) {
          toast(<FormattedMessage {...messages.updateTenantError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (tenantActions.delete.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteTenantSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (tenantActions.delete.error.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteTenantError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (leaseUnitActions.create.success.match(action)) {
          toast(
            <FormattedMessage
              {...messages.createLeaseUnitSuccess}
              values={{
                name: <strong>{action.payload.name}</strong>,
              }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        } else if (leaseUnitActions.create.error.match(action)) {
          toast.error(<FormattedMessage {...messages.createLeaseUnitError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (leaseUnitActions.update.success.match(action)) {
          toast(
            <FormattedMessage
              {...messages.updateLeaseUnitSuccess}
              values={{
                name: <strong>{action.payload.name}</strong>,
              }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        } else if (leaseUnitActions.update.error.match(action)) {
          toast(<FormattedMessage {...messages.updateLeaseUnitError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (leaseUnitActions.delete.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteLeaseUnitSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (leaseUnitActions.delete.error.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteLeaseUnitError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (
          (leasesActions.create.error.match(action) ||
            leaseActions.create.error.match(action) ||
            leaseActions.update.error.match(action)) &&
          action.payload instanceof ConflictError &&
          action.payload.code === 'lease.periodOverlap'
        ) {
          toast.error(<FormattedMessage {...messages.leasePeriodOverlap} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (leasesActions.create.success.match(action)) {
          toast(<FormattedMessage {...messages.createLeasesSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (leasesActions.create.error.match(action)) {
          toast.error(<FormattedMessage {...messages.createLeasesError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (leaseActions.create.success.match(action)) {
          toast(<FormattedMessage {...messages.createLeaseSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (leaseActions.create.error.match(action)) {
          toast.error(<FormattedMessage {...messages.createLeaseError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (leaseActions.update.success.match(action)) {
          toast(<FormattedMessage {...messages.updateLeaseSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (leaseActions.update.error.match(action)) {
          toast.error(<FormattedMessage {...messages.updateLeaseError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (readingActions.remote.value.delete.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteRecordSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (floorPlanActions.create.success.match(action)) {
          toast(<FormattedMessage {...messages.createFloorPlanSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (floorPlanActions.create.error.match(action)) {
          toast.error(<FormattedMessage {...messages.createFloorPlanError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (floorPlanActions.update.success.match(action)) {
          toast(<FormattedMessage {...messages.updateFloorPlanSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (floorPlanActions.update.error.match(action)) {
          toast.error(<FormattedMessage {...messages.updateFloorPlanError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (floorPlanActions.delete.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteFloorPlanSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (floorPlanActions.delete.error.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteFloorPlanError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (billingSettingsActions.delete.error.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteBillingSettingsError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (billingSettingsActions.delete.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteBillingSettingsSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (billingSettingsActions.create.success.match(action)) {
          toast(
            <FormattedMessage
              {...messages.createBillingSettingsSuccess}
              values={{
                name: <strong>{action.payload.name}</strong>,
              }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        } else if (billingSettingsActions.create.error.match(action)) {
          toast.error(<FormattedMessage {...messages.createBillingSettingsError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (billingSettingsActions.update.success.match(action)) {
          toast(
            <FormattedMessage
              {...messages.updateBillingSettingsSuccess}
              values={{
                name: <strong>{action.payload.name}</strong>,
              }}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: action.type,
            },
          );
        } else if (billingSettingsActions.update.error.match(action)) {
          toast.error(<FormattedMessage {...messages.updateBillingSettingsError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (billingActions.delete.success.match(action)) {
          toast(<FormattedMessage {...messages.deleteBillingSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (billingActions.delete.error.match(action)) {
          toast.error(<FormattedMessage {...messages.deleteBillingError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (billingActions.calculate.success.match(action)) {
          toast(<FormattedMessage {...messages.calculateBillingSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (billingActions.calculate.error.match(action)) {
          toast.error(<FormattedMessage {...messages.calculateBillingError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (billingActions.preview.error.match(action)) {
          toast.error(<FormattedMessage {...messages.calculateBillingError} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        } else if (conflictErrorAction.match(action)) {
          const { code } = action.payload;
          const messageDescriptorId = `conflictError.${code}`;

          toast.error(<FormattedMessage id={messageDescriptorId} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        }

        // SUPPORT - BUG REPORT
        else if (bugReportActions.create.success.match(action)) {
          toast(<FormattedMessage {...messages.createBugReportSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
        }

        // SUPPORT - ASK NEW METER MODEL
        else if (askNewMeterModelActions.create.success.match(action)) {
          toast(<FormattedMessage {...messages.askNewMeterModelSuccess} />, {
            position: toast.POSITION.BOTTOM_RIGHT,
            toastId: action.type,
          });
          break;
        }
    }

    return result;
  };
